import { track } from '@vercel/analytics';

const KEY = 'cartlens_blog_intent_v1';
const TTL = 24 * 60 * 60 * 1000;
const placements = ['navbar', 'menu', 'intro', 'outro'] as const;
type Placement = typeof placements[number];
type Stage = 'signup_view' | 'signup_complete' | 'first_scan' | 'first_archive';
type Intent = {
  article: string; placement: Placement; startedAt: number;
  userId?: string; dismissed?: boolean; stages: Stage[];
};

// Only public blog paths enter telemetry; never queries, receipt content or identity.
function validArticle(path: string): boolean {
  return /^\/blog(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)?$/.test(path) && path.length <= 200;
}

function emit(event: string, intent: Intent): void {
  const data = { article: intent.article, placement: intent.placement };
  try { track(`blog_${event}`, data); } catch { /* analytics cannot block navigation */ }
  try { if (typeof window.gtag === 'function') window.gtag('event', `blog_${event}`, data); } catch { /* optional analytics */ }
}

function save(intent: Intent): void {
  try { sessionStorage.setItem(KEY, JSON.stringify(intent)); } catch { /* optional storage */ }
}

export function readBlogIntent(): Intent | null {
  try {
    const intent = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (!intent || !validArticle(intent.article) || !placements.includes(intent.placement)
      || !Number.isFinite(intent.startedAt) || intent.startedAt > Date.now()
      || Date.now() - intent.startedAt > TTL || !Array.isArray(intent.stages)) return null;
    return intent;
  } catch { return null; }
}

export function blogSignupHref(article: string, placement: Placement): string {
  if (!validArticle(article)) return '/sign-up';
  return `/sign-up?${new URLSearchParams({ blog: article, placement })}`;
}

export function beginBlogSignup(article: string, placement: Placement): void {
  if (!validArticle(article)) return;
  const intent: Intent = { article, placement, startedAt: Date.now(), stages: [] };
  save(intent);
  emit('cta_click', intent);
}

// Query context also works when a reader opens a CTA in a new tab.
export function captureBlogSignupView(search: string): Intent | null {
  const params = new URLSearchParams(search);
  const article = params.get('blog') || '';
  const placement = params.get('placement') as Placement;
  let intent = readBlogIntent();
  if (validArticle(article) && placements.includes(placement)
    && (!intent || intent.article !== article || intent.placement !== placement)) {
    intent = { article, placement, startedAt: Date.now(), stages: [] };
  }
  if (intent && !intent.stages.includes('signup_view')) {
    intent.stages.push('signup_view');
    save(intent);
    emit('signup_view', intent);
  }
  return intent;
}

export function blogIntentForUser(userId: string, createdAt: Date | null | undefined): Intent | null {
  const intent = readBlogIntent();
  const created = createdAt ? new Date(createdAt).getTime() : NaN;
  // A returning subscriber who reads an article is not a newly converted reader.
  if (!intent || !Number.isFinite(created) || created > Date.now() + 60_000 || created < intent.startedAt - 60_000
    || Date.now() - created > TTL || (intent.userId && intent.userId !== userId)) return null;
  return { ...intent, userId };
}

export function recordBlogStage(stage: Exclude<Stage, 'signup_view'>, userId: string, createdAt: Date | null | undefined): void {
  const intent = blogIntentForUser(userId, createdAt);
  if (!intent || intent.stages.includes(stage)) return;
  intent.stages.push(stage);
  save(intent);
  emit(stage, intent);
}

export function dismissBlogGuide(): void {
  const intent = readBlogIntent();
  if (intent) save({ ...intent, dismissed: true });
}
